import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Feather, Users, BookOpen } from 'lucide-react';

export function BusinessPage() {
  return (
    <div className="min-h-screen bg-[#ddd9d9]">
      {/* Hero Section */}
      <div className="relative bg-[#540a17] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Talent Made Simple:
                <br />
                Student Interns for
                <br />
                Every Small Business
                <br />
                Need
              </h1>
              <p className="text-lg text-white/90 mb-8">
                Tezhly for Businesses connects small businesses and solo founders with a curated database of skilled tech interns. Subscribe to gain affordable, hassle-free access to top talent ready to help your business grow and thrive.
              </p>
              <Link
                to="/subscription"
                className="inline-block px-8 py-4 bg-white text-[#540a17] rounded-full font-bold hover:bg-opacity-90 transition-colors"
              >
                SUBSCRIBE NOW
              </Link>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Business team working"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* How it Works Section */}
      <section className="py-20 bg-[#ddd9d9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#800020] mb-4">How Tezhly for Business Works</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our streamlined process ensures that businesses receive the right talent to help them grow while providing students with valuable hands-on experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Step 1-3 */}
            <div className="bg-white p-8 rounded-xl shadow-lg relative">
              <div className="absolute -top-4 left-8 bg-[#800020] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-[#800020] mt-4 mb-4">Submit Your Request</h3>
              <p className="text-gray-600">
                Fill out our quick online form with your business details, required skills, and support needs. This helps us understand how we can best assist your business.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg relative">
              <div className="absolute -top-4 left-8 bg-[#800020] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-[#800020] mt-4 mb-4">Schedule a Consultation</h3>
              <p className="text-gray-600">
                After submission, schedule a one-on-one consultation with our team to discuss your specific needs, whether it's marketing, tech support, or operations.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg relative">
              <div className="absolute -top-4 left-8 bg-[#800020] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-[#800020] mt-4 mb-4">Matching with a Student Intern</h3>
              <p className="text-gray-600">
                We carefully match you with a student intern based on your required skill set and industry needs, ensuring a valuable partnership.
              </p>
            </div>

            {/* Step 4-5 */}
            <div className="bg-white p-8 rounded-xl shadow-lg relative">
              <div className="absolute -top-4 left-8 bg-[#800020] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <h3 className="text-xl font-bold text-[#800020] mt-4 mb-4">Rotational Internship Model</h3>
              <p className="text-gray-600">
                Every two months, receive a new intern to benefit from fresh perspectives and different skill sets while providing diverse experiences to students.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg relative">
              <div className="absolute -top-4 left-8 bg-[#800020] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                5
              </div>
              <h3 className="text-xl font-bold text-[#800020] mt-4 mb-4">Ongoing Support and Feedback</h3>
              <p className="text-gray-600">
                Receive continuous support from Tezhly. Provide feedback at the end of each internship cycle to help us refine future matches.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-[#800020] mb-4">End Goal: A Perfect Match</h3>
              <p className="text-gray-600">
                Our ultimate goal is to create mutually beneficial relationships between businesses and student interns, fostering growth, learning, and professional development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#800020] mb-4">Submit Your Request</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Begin by filling out our quick online form to express interest in the program. We'll match you with skilled interns who can help grow your business.
            </p>
          </div>

          <div className="max-w-2xl mx-auto bg-[#ddd9d9] rounded-xl p-8 shadow-lg">
            <form className="space-y-6">
              <div>
                <label htmlFor="business-name" className="block text-sm font-medium text-[#800020] mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  id="business-name"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
                  required
                />
              </div>

              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-[#800020] mb-2">
                  Industry
                </label>
                <select
                  id="industry"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
                  required
                >
                  <option value="">Select Industry</option>
                  <option value="technology">Technology</option>
                  <option value="marketing">Marketing</option>
                  <option value="finance">Finance</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="education">Education</option>
                  <option value="retail">Retail</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="skills-needed" className="block text-sm font-medium text-[#800020] mb-2">
                  Required Skills
                </label>
                <select
                  id="skills-needed"
                  multiple
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020] h-32"
                  required
                >
                  <option value="web-development">Web Development</option>
                  <option value="digital-marketing">Digital Marketing</option>
                  <option value="data-analysis">Data Analysis</option>
                  <option value="graphic-design">Graphic Design</option>
                  <option value="content-writing">Content Writing</option>
                  <option value="social-media">Social Media Management</option>
                  <option value="seo">SEO</option>
                  <option value="project-management">Project Management</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">Hold Ctrl/Cmd to select multiple skills</p>
              </div>

              <div>
                <label htmlFor="support-type" className="block text-sm font-medium text-[#800020] mb-2">
                  Type of Support Needed
                </label>
                <textarea
                  id="support-type"
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
                  placeholder="Describe the type of support you're looking for..."
                  required
                ></textarea>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#800020] mb-2">
                  Business Email
                </label>
                <input
                  type="email"
                  id="email"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#800020] mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full px-8 py-4 bg-[#800020] text-white rounded-full font-bold hover:bg-opacity-90 transition-colors"
              >
                Submit Request
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl text-center">
              <div className="flex justify-center mb-4">
                <Globe className="h-12 w-12 text-[#800020]" />
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Access Top Talent</h3>
              <p className="text-gray-600">
                A curated database of skilled intern talent across various disciplines.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl text-center">
              <div className="flex justify-center mb-4">
                <Feather className="h-12 w-12 text-[#800020]" />
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Affordable Monthly Subscription</h3>
              <p className="text-gray-600">
                Flexible pricing designed for small businesses and solo founders.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl text-center">
              <div className="flex justify-center mb-4">
                <Users className="h-12 w-12 text-[#800020]" />
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Customized Matches</h3>
              <p className="text-gray-600">
                Get matched with interns who align with your business goals.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl text-center">
              <div className="flex justify-center mb-4">
                <BookOpen className="h-12 w-12 text-[#800020]" />
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Internship Support</h3>
              <p className="text-gray-600">
                Resources to help you onboard and manage interns effectively.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}